import { Monitor, Moon, Sun } from "lucide-react";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { useTheme } from "@/hooks/useTheme";
import type { ThemePreference } from "@/lib/theme";

/**
 * CIR-2026-361 — Appearance (System / Light / Dark) control.
 *
 * A native <select> for robust keyboard + screen-reader support: it has a
 * visible "Appearance" label (associated via htmlFor/id), is keyboard-operable
 * by default, exposes a focus-visible ring from --ring, and carries an
 * aria-label so the control is self-describing when the label is visually
 * compact. Changing the value persists `spire-theme` and toggles `.dark` on
 * <html> immediately (no reload), via useTheme.
 */
const PREFERENCE_ICON: Record<ThemePreference, typeof Monitor> = {
  system: Monitor,
  light: Sun,
  dark: Moon,
};

export function ThemeToggle({ className }: { className?: string }) {
  const { preference, setPreference } = useTheme();
  // eslint-disable-next-line security/detect-object-injection -- safe: key is a typed ThemePreference union, not user input (CIR-2026-296)
  const Icon = PREFERENCE_ICON[preference];

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <Label htmlFor="appearance-select" className="flex items-center gap-1.5 text-muted-foreground">
        <Icon className="h-4 w-4" aria-hidden="true" />
        <span className="hidden sm:inline">Appearance</span>
      </Label>
      <select
        id="appearance-select"
        aria-label="Appearance theme"
        value={preference}
        onChange={(e) => setPreference(e.target.value as ThemePreference)}
        className={cn(
          "h-9 rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
          "disabled:cursor-not-allowed disabled:opacity-50",
        )}
      >
        <option value="system">System</option>
        <option value="light">Light</option>
        <option value="dark">Dark</option>
      </select>
    </div>
  );
}
